import { CandidateItemDto, CandidateProfileDto, CandidateStatusDto } from '@/api/generated';
import ButtonWithLoading from '@/components/ButtonWithLoading.comp.tsx';
import Loader from '@/components/Loader.tsx';
import { useUserContext } from '@/utils/providers/userContext/useUserContext.tsx';
import { useTranslationWithPrefix } from '@/utils/useTranslationWithPrefix.ts';
import { initials, statusTone, TONE_STYLES } from '@/views/admin/candidates/candidateStatus.ts';
import { useGetCandidateProfile } from '@/views/admin/candidates/useCandidatesApi.util.ts';
import { AdminActionsDropdown } from '@/views/admin/components/AdminActionsDropdown.comp.tsx';
import { COLORS, formatNumber, SCORE_COLORS } from '@/views/admin/dashboard/dashboardTheme.ts';
import { useDownloadCandidateCv } from '@/views/admin/util/useDownloadCandidateCv.util.ts';
import { useDownloadCandidateFullAudit } from '@/views/admin/util/useDownloadCandidateFullAudit.util.ts';
import { useDownloadCandidateProcessCv } from '@/views/admin/util/useDownloadCandidateProcessCv.util.ts';
import React, { ReactNode, useEffect } from 'react';
import { Alert } from 'react-bootstrap';
import { createPortal } from 'react-dom';
import {
  IoClose,
  IoDocumentTextOutline,
  IoDownloadOutline,
  IoLogoGithub,
  IoLogoLinkedin,
} from 'react-icons/io5';
import { TbFileCv } from 'react-icons/tb';

// files exist only once the quiz result is decided
const STATUSES_WITH_FILES: CandidateStatusDto[] = [
  CandidateStatusDto.SentToERecruiter,
  CandidateStatusDto.PositiveFeedback,
  CandidateStatusDto.NegativeFeedback,
  CandidateStatusDto.PositiveFeedbackSent,
  CandidateStatusDto.NegativeFeedbackSent,
];

const Section: React.FC<{ children: ReactNode; title: string }> = ({ children, title }) => (
  <section className="px-6 py-5" style={{ borderTop: `1px solid ${COLORS.grid}` }}>
    <h3
      className="m-0 mb-3 text-xs font-bold uppercase tracking-wider"
      style={{ color: COLORS.inkMuted }}
    >
      {title}
    </h3>
    {children}
  </section>
);

const Field: React.FC<{ label: string; value?: ReactNode }> = ({ label, value }) => (
  <div className="min-w-0">
    <dt className="text-xs" style={{ color: COLORS.inkMuted }}>
      {label}
    </dt>
    <dd className="m-0 truncate text-sm" style={{ color: COLORS.ink }}>
      {value ?? '—'}
    </dd>
  </div>
);

const Tile: React.FC<{ label: string; note?: string; value: ReactNode }> = ({
  label,
  note,
  value,
}) => (
  <div className="min-w-0 p-3" style={{ backgroundColor: '#f7f7f7' }}>
    <div className="text-xs" style={{ color: COLORS.inkMuted }}>
      {label}
    </div>
    <div className="truncate text-lg font-bold" style={{ color: COLORS.ink }}>
      {value}
    </div>
    {note && (
      <div className="truncate text-xs" style={{ color: COLORS.inkSecondary }}>
        {note}
      </div>
    )}
  </div>
);

const SkillLevel: React.FC<{ label: string; level: number }> = ({ label, level }) => (
  <span className="inline-flex items-center gap-1" title={label}>
    {[1, 2, 3, 4].map(step => (
      <span
        className="inline-block h-2 w-2 rounded-full"
        key={step}
        style={{ backgroundColor: step <= level ? COLORS.orange : '#e3e3e3' }}
      />
    ))}
  </span>
);

interface Props {
  candidate: CandidateItemDto;
  language: string;
  onActionSuccess: () => void;
  onClose: () => void;
}

export const CandidateProfilePanel: React.FC<Props> = ({
  candidate,
  language,
  onActionSuccess,
  onClose,
}) => {
  const { t } = useTranslationWithPrefix('views.admin');
  const { user } = useUserContext();
  const { error, isLoading, profile } = useGetCandidateProfile(
    user?.userId ?? '',
    candidate.candidateId,
  );
  const { downloadCandidateCv, isDownloading } = useDownloadCandidateCv();
  const { downloadCandidateProcessCv, isDownloading: isDownloadingProcess } =
    useDownloadCandidateProcessCv();
  const { downloadCandidateFullAudit, isDownloading: isDownloadingAudit } =
    useDownloadCandidateFullAudit();

  useEffect(() => {
    const onKeyDown = (event: KeyboardEvent) => event.key === 'Escape' && onClose();
    document.addEventListener('keydown', onKeyDown);
    return () => document.removeEventListener('keydown', onKeyDown);
  }, [onClose]);

  const tone = statusTone(candidate.status);
  const filesAvailable = STATUSES_WITH_FILES.includes(candidate.status);
  const dateTime = new Intl.DateTimeFormat(language, { dateStyle: 'medium', timeStyle: 'short' });
  const date = (value?: string) => (value ? dateTime.format(new Date(value)) : undefined);
  const ids = { adminUserId: user?.userId ?? '', candidateId: candidate.candidateId };

  // a portal: the app wrapper is scaled with a CSS transform, which would make position: fixed relative to it
  return createPortal(
    <div className="fixed inset-0" style={{ zIndex: 1040 }}>
      <button
        aria-label={t('candidates.profile.close')}
        className="absolute inset-0 cursor-default border-0"
        onClick={onClose}
        style={{ backgroundColor: 'rgba(0,0,0,0.25)' }}
        type="button"
      />
      <aside
        aria-label={`${candidate.firstName} ${candidate.lastName}`}
        className="absolute top-0 right-0 flex h-full flex-col overflow-y-auto bg-white"
        role="dialog"
        style={{ boxShadow: '-8px 0 32px rgba(0,0,0,0.18)', maxWidth: '100vw', width: 640 }}
      >
        <header
          className="sticky top-0 z-10 bg-white px-6 pt-5 pb-4"
          style={{ borderBottom: `1px solid ${COLORS.grid}` }}
        >
          <div className="flex items-start gap-4">
            <span
              aria-hidden
              className="inline-flex h-12 w-12 shrink-0 items-center justify-center rounded-full font-bold text-white"
              style={{ backgroundColor: COLORS.ink }}
            >
              {initials(candidate.firstName, candidate.lastName)}
            </span>
            <div className="min-w-0 flex-1">
              <h2 className="m-0 truncate text-xl font-bold" style={{ color: COLORS.ink }}>
                {candidate.firstName} {candidate.lastName}
              </h2>
              <div
                className="mt-1 flex flex-wrap gap-x-4 text-sm"
                style={{ color: COLORS.inkSecondary }}
              >
                <a href={`mailto:${candidate.email}`} style={{ color: 'inherit' }}>
                  {candidate.email}
                </a>
                {candidate.phone && (
                  <a href={`tel:${candidate.phone}`} style={{ color: 'inherit' }}>
                    {candidate.phone}
                  </a>
                )}
              </div>
              <span
                className="mt-2 inline-flex items-center px-2 py-1 text-xs font-bold"
                style={{ ...TONE_STYLES[tone], borderRadius: 4 }}
              >
                {t(`table.statuses.${candidate.status}`)}
              </span>
            </div>
            <button
              aria-label={t('candidates.profile.close')}
              className="cursor-pointer border-0 bg-transparent p-1"
              onClick={onClose}
              type="button"
            >
              <IoClose size={24} />
            </button>
          </div>
          <div className="mt-4 flex flex-wrap items-center gap-2">
            <ButtonWithLoading
              className="table-action-button"
              disabled={!filesAvailable}
              isLoading={isDownloading}
              onClick={() => void downloadCandidateCv(ids)}
              tooltip={{ title: t('tooltips.downloadCv') }}
            >
              <TbFileCv />
            </ButtonWithLoading>
            <ButtonWithLoading
              className="table-action-button"
              disabled={!filesAvailable}
              isLoading={isDownloadingProcess}
              onClick={() => void downloadCandidateProcessCv(ids)}
              tooltip={{ title: t('tooltips.downloadProcessCv') }}
            >
              <IoDocumentTextOutline />
            </ButtonWithLoading>
            <ButtonWithLoading
              className="table-action-button"
              isLoading={isDownloadingAudit}
              onClick={() =>
                void downloadCandidateFullAudit({
                  adminUserId: ids.adminUserId,
                  email: candidate.email,
                })
              }
              tooltip={{ title: t('tooltips.downloadFullAudit') }}
            >
              <IoDownloadOutline />
            </ButtonWithLoading>
            <div className="ml-auto">
              <AdminActionsDropdown candidate={candidate} onSuccess={onActionSuccess} />
            </div>
          </div>
        </header>

        {isLoading && <Loader className="min-h-[200px]" />}
        {error && (
          <Alert className="m-6" variant="danger">
            {t('candidates.profile.loadError')}
          </Alert>
        )}
        {profile && (
          <ProfileBody candidate={candidate} date={date} language={language} profile={profile} />
        )}
      </aside>
    </div>,
    document.body,
  );
};

const ProfileBody: React.FC<{
  candidate: CandidateItemDto;
  date: (value?: string) => string | undefined;
  language: string;
  profile: CandidateProfileDto;
}> = ({ candidate, date, language, profile }) => {
  const { t } = useTranslationWithPrefix('views.admin');
  const languageNames = new Intl.DisplayNames([language], { type: 'language' });
  const quiz = profile.quiz ?? undefined;
  const levels = t('candidates.profile.skillLevels', {
    returnObjects: true,
  }) as unknown as string[];
  const skillTypes = [...new Set(profile.skills.map(skill => skill.type))];
  const yesNo = (value?: boolean) =>
    value === undefined || value === null
      ? undefined
      : value
        ? t('candidates.profile.yes')
        : t('candidates.profile.no');
  const timelineText = (type: string) => {
    const key = `candidates.timelineEvents.${type}`;
    const text = t(key);
    return text === key || text.endsWith(type) ? t('candidates.timelineEvents.other') : text;
  };

  return (
    <>
      <div className="grid grid-cols-2 gap-3 px-6 py-5 sm:grid-cols-4">
        <Tile
          label={t('candidates.profile.points')}
          note={quiz?.score ? t(`table.quizScores.${quiz.score}`) : undefined}
          value={
            quiz?.points !== undefined && quiz?.points !== null
              ? formatNumber(quiz.points, language)
              : '—'
          }
        />
        <Tile
          label={t('candidates.profile.activeTime')}
          value={
            quiz?.activeMinutes !== undefined && quiz?.activeMinutes !== null
              ? t('candidates.profile.minutes', {
                  value: formatNumber(quiz.activeMinutes, language),
                })
              : '—'
          }
        />
        <Tile
          label={t('candidates.profile.registered')}
          value={new Intl.DateTimeFormat(language, { dateStyle: 'medium' }).format(
            new Date(profile.registeredAt),
          )}
        />
        <Tile
          label={t('candidates.profile.language')}
          value={
            profile.preferredLanguage ? (languageNames.of(profile.preferredLanguage) ?? '—') : '—'
          }
        />
      </div>

      <Section title={t('candidates.profile.categories')}>
        {quiz && quiz.categories.length > 0 ? (
          <ul className="m-0 grid list-none gap-3 p-0">
            {quiz.categories.map(category => {
              const share = category.maxPoints > 0 ? category.points / category.maxPoints : 0;
              return (
                <li key={category.category}>
                  <div className="flex items-baseline justify-between gap-3 text-sm">
                    <span style={{ color: COLORS.ink }}>{category.label}</span>
                    <span
                      className="whitespace-nowrap text-xs"
                      style={{ color: COLORS.inkSecondary }}
                    >
                      <strong style={{ color: COLORS.ink }}>
                        {formatNumber(category.points, language, 1)}
                      </strong>{' '}
                      / {category.maxPoints}
                    </span>
                  </div>
                  <div className="mt-1 h-2 w-full" style={{ backgroundColor: COLORS.grid }}>
                    <div
                      className="h-full transition-[width] duration-700 ease-out"
                      style={{
                        backgroundColor: quiz.score ? SCORE_COLORS[quiz.score] : COLORS.orange,
                        borderRadius: '0 4px 4px 0',
                        width: `${Math.min(100, share * 100)}%`,
                      }}
                    />
                  </div>
                </li>
              );
            })}
          </ul>
        ) : (
          <p className="m-0 text-sm" style={{ color: COLORS.inkSecondary }}>
            {quiz ? '—' : t('candidates.profile.noQuiz')}
          </p>
        )}
        {quiz && (
          <dl className="m-0 mt-4 grid grid-cols-2 gap-3">
            <Field label={t('candidates.profile.started')} value={date(quiz.startedAt)} />
            <Field
              label={t('candidates.profile.finished')}
              value={date(quiz.finishedAt ?? undefined)}
            />
          </dl>
        )}
      </Section>

      <Section title={t('candidates.profile.studies')}>
        <dl className="m-0 grid grid-cols-2 gap-3">
          <Field
            label={t('candidates.profile.university')}
            value={profile.university ?? undefined}
          />
          <Field
            label={t('candidates.profile.fieldOfStudy')}
            value={profile.fieldOfStudy ?? undefined}
          />
          <Field
            label={t('candidates.profile.studentStatus')}
            value={profile.studentStatus ?? undefined}
          />
          <Field
            label={t('candidates.profile.typeOfStudy')}
            value={profile.typeOfStudy ?? undefined}
          />
          <Field
            label={t('candidates.profile.yearOfStudy')}
            value={profile.yearOfStudy ?? undefined}
          />
          <Field
            label={t('candidates.profile.location')}
            value={profile.location ?? candidate.location ?? undefined}
          />
          <Field
            label={t('candidates.profile.under30')}
            value={yesNo(profile.lessThan30Years ?? undefined)}
          />
          {profile.fairNote && (
            <Field label={t('candidates.profile.fair')} value={profile.fairNote} />
          )}
        </dl>
      </Section>

      <Section title={t('candidates.profile.areas')}>
        <ol className="m-0 flex list-none flex-wrap gap-2 p-0">
          {profile.areas.map(area => (
            <li
              className="inline-flex items-center gap-2 px-2 py-1 text-sm"
              key={area.name}
              style={{
                backgroundColor: area.priority === 1 ? '#fff1e3' : '#f4f4f4',
                borderRadius: 4,
              }}
            >
              <strong style={{ color: area.priority === 1 ? '#8a3c00' : COLORS.inkSecondary }}>
                {area.priority}.
              </strong>
              <span style={{ color: COLORS.ink }}>{area.label}</span>
            </li>
          ))}
        </ol>
        {profile.areasJustification && (
          <figure className="m-0 mt-4">
            <figcaption className="mb-1 text-xs" style={{ color: COLORS.inkMuted }}>
              {t('candidates.profile.justification')}
            </figcaption>
            <blockquote
              className="m-0 py-1 pl-3 text-sm whitespace-pre-line"
              style={{ borderLeft: `3px solid ${COLORS.orange}`, color: COLORS.inkSecondary }}
            >
              {profile.areasJustification}
            </blockquote>
          </figure>
        )}
      </Section>

      {profile.skills.length > 0 && (
        <Section title={t('candidates.profile.skills')}>
          <div className="grid gap-4">
            {skillTypes.map(type => (
              <div key={type}>
                <div className="mb-2 text-sm font-bold" style={{ color: COLORS.ink }}>
                  {t(`candidates.profile.skillTypes.${type}`)}
                </div>
                <ul className="m-0 grid list-none grid-cols-1 gap-x-6 gap-y-1.5 p-0 sm:grid-cols-2">
                  {profile.skills
                    .filter(skill => skill.type === type)
                    .map(skill => (
                      <li
                        className="flex items-center justify-between gap-3 text-sm"
                        key={skill.name}
                      >
                        <span
                          className="truncate"
                          style={{ color: COLORS.inkSecondary }}
                          title={skill.label}
                        >
                          {skill.label}
                        </span>
                        <SkillLevel
                          label={levels[skill.level] ?? String(skill.level)}
                          level={skill.level}
                        />
                      </li>
                    ))}
                </ul>
              </div>
            ))}
          </div>
        </Section>
      )}

      {(profile.aboutYourself || profile.linkedInUrl || profile.githubUrl) && (
        <Section title={t('candidates.profile.about')}>
          {profile.aboutYourself && (
            <p
              className="m-0 mb-3 text-sm whitespace-pre-line"
              style={{ color: COLORS.inkSecondary }}
            >
              {profile.aboutYourself}
            </p>
          )}
          <div className="flex flex-wrap gap-4 text-sm">
            {profile.linkedInUrl && (
              <a
                className="inline-flex items-center gap-1"
                href={profile.linkedInUrl}
                rel="noopener noreferrer"
                target="_blank"
              >
                <IoLogoLinkedin aria-hidden /> LinkedIn
              </a>
            )}
            {profile.githubUrl && (
              <a
                className="inline-flex items-center gap-1"
                href={profile.githubUrl}
                rel="noopener noreferrer"
                target="_blank"
              >
                <IoLogoGithub aria-hidden /> GitHub
              </a>
            )}
          </div>
        </Section>
      )}

      <Section title={t('candidates.profile.timeline')}>
        <ol className="m-0 list-none p-0">
          {profile.timeline.map((event, index) => (
            <li
              className="relative flex gap-3 pb-4 pl-5"
              key={`${event.type}-${event.occurredAt}-${index}`}
            >
              <span
                aria-hidden
                className="absolute top-1.5 left-0 inline-block h-2.5 w-2.5 rounded-full"
                style={{ backgroundColor: index === 0 ? COLORS.orange : '#c9c9c9' }}
              />
              {index < profile.timeline.length - 1 && (
                <span
                  aria-hidden
                  className="absolute top-4 bottom-0 left-[4px] w-px"
                  style={{ backgroundColor: COLORS.grid }}
                />
              )}
              <div className="min-w-0">
                <div className="text-sm" style={{ color: COLORS.ink }}>
                  {timelineText(event.type)}
                </div>
                <div className="text-xs" style={{ color: COLORS.inkMuted }}>
                  {date(event.occurredAt)}
                </div>
              </div>
            </li>
          ))}
        </ol>
      </Section>
    </>
  );
};
